import { type RefObject, useEffect, useState } from 'react'

/* What the app draws while it waits: the core spinner (also inside `LoaderOverlay`) and the
   skeleton blocks. */
const LOADING_SELECTOR = '[data-testid="loader"], [data-skeleton]'

/* A content that never shows a loader is let through after a beat rather than at once, so a
   section that mounts its loader a frame late is still covered from the start. */
const FIRST_CHECK_MS = 150

/* Sections often load in a chain — one spinner goes, the next one comes. The curtain waits this
   long with no loader in sight before it lifts, so the chain reads as one wait. */
const SETTLE_MS = 300

/* A loader that never goes away is the section's problem, not the curtain's: past this the
   curtain lifts and shows what there is. */
const MAX_WAIT_MS = 15000

/* A hidden `LoaderOverlay` keeps its spinner in the DOM, so only what is drawn counts. */
const isDrawn = (element: Element & { checkVisibility?: () => boolean }) =>
  typeof element.checkVisibility === 'function'
    ? element.checkVisibility()
    : element.getClientRects().length > 0

const hasLoader = (root: HTMLElement) =>
  Array.from(root.querySelectorAll(LOADING_SELECTOR)).some(isDrawn)

/**
 * Whether the content under `ref` is still loading for the first time: true from the start, and
 * false once no drawn loader has stood in it for a moment. It only follows the first load — a
 * later spinner (a save, a filter) belongs to the content, which shows its own — and a reload the
 * owner holds it for with `isHeld`: loading for as long as it is held, then until the content's
 * own loaders settle, as on the first load.
 *
 * The node is watched for mutations, so whatever reacts to the answer must be drawn outside it,
 * or each answer would mutate the node and ask again.
 */
export const useContentLoading = (ref: RefObject<HTMLElement | null>, isHeld = false) => {
  const [isLoading, setIsLoading] = useState(true)
  const [wasHeld, setWasHeld] = useState(isHeld)

  /* A new hold brings the loading state back at once, in the same render. */
  if (isHeld !== wasHeld) {
    setWasHeld(isHeld)

    if (isHeld) {
      setIsLoading(true)
    }
  }

  useEffect(() => {
    const root = ref.current

    if (!root || !isLoading) {
      return
    }

    /* Held, it waits for the owner; past the longest wait it lifts all the same. */
    if (isHeld) {
      const heldTooLong = window.setTimeout(() => setIsLoading(false), MAX_WAIT_MS)

      return () => window.clearTimeout(heldTooLong)
    }

    let settleTimer: number | undefined
    let isReady = false

    const finish = () => {
      if (isReady) {
        return
      }

      isReady = true
      setIsLoading(false)
    }

    const check = () => {
      window.clearTimeout(settleTimer)

      if (!hasLoader(root)) {
        settleTimer = window.setTimeout(finish, SETTLE_MS)
      }
    }

    const observer = new MutationObserver(check)
    const firstCheck = window.setTimeout(() => {
      observer.observe(root, {
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden'],
        childList: true,
        subtree: true,
      })

      if (hasLoader(root)) {
        return
      }

      finish()
    }, FIRST_CHECK_MS)
    const maxWait = window.setTimeout(finish, MAX_WAIT_MS)

    return () => {
      observer.disconnect()
      window.clearTimeout(firstCheck)
      window.clearTimeout(settleTimer)
      window.clearTimeout(maxWait)
    }
  }, [isHeld, isLoading, ref])

  return isLoading
}
